import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  fetchPricingSchedule,
  createAttendeeOrder,
  verifyAttendeePayment,
  loadRazorpayScript,
  PricingScheduleResponse,
  fetchAttendeeBadge,
  BadgeDetails,
} from '../lib/api';
import { DigitalBadge } from '../components/DigitalBadge';
import { eventInfo } from '../data';
import { isAppStoreReaderMode } from '../lib/nativeMobile';
import {
  Ticket,
  CheckCircle2,
  QrCode,
  ArrowRight,
  CreditCard,
  Building2,
  User,
  Mail,
  Phone,
  Receipt,
  ChevronRight,
  TrendingDown,
  UserCheck,
  UserPlus,
  Trash2,
  Sparkles,
  ShieldCheck,
  Briefcase,
  Users,
} from 'lucide-react';
import confetti from 'canvas-confetti';

export interface AttendeeEntry {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  jobTitle: string;
  tier: 'trade_visitor' | 'conference_delegate' | 'vip';
}

export function RegisterPage() {
  const navigate = useNavigate();

  // Pricing Schedule State
  const [scheduleData, setScheduleData] = useState<PricingScheduleResponse | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Booker / Purchaser State
  const [isBookerAttending, setIsBookerAttending] = useState<boolean>(true);
  const [bookerName, setBookerName] = useState('');
  const [bookerEmail, setBookerEmail] = useState('');
  const [bookerPhone, setBookerPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [gstin, setGstin] = useState('');

  // Attendees Roster State
  const [attendees, setAttendees] = useState<AttendeeEntry[]>([
    {
      id: 'attendee-1',
      fullName: '',
      email: '',
      phone: '',
      jobTitle: '',
      tier: 'conference_delegate',
    },
  ]);

  // Submission & Processing State
  const [processing, setProcessing] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Pass Lookup Tab State
  const [activeTab] = useState<'purchase' | 'lookup'>('purchase');
  const [lookupToken, setLookupToken] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [foundBadge, setFoundBadge] = useState<BadgeDetails | null>(null);

  useEffect(() => {
    async function loadPricing() {
      try {
        const data = await fetchPricingSchedule();
        setScheduleData(data);
      } catch (err) {
        console.warn('Failed to load pricing schedule from API, using fallback', err);
        setScheduleData({
          activePhase: {
            id: 'super_early',
            name: 'Super Early Bird',
            badge: 'Save up to 67%',
            description: 'Launch concession rate for early registrants',
            startDate: '2026-08-01T00:00:00.000Z',
            endDate: '2026-12-31T23:59:59.999Z',
            prices: {
              trade_visitor: 999,
              conference_delegate: 4999,
              vip: 11999,
            },
            savingsVsSpot: {
              trade_visitor: 2001,
              conference_delegate: 5501,
              vip: 8001,
            },
          },
          allPhases: [],
          serverTime: new Date().toISOString(),
          gstPercent: 18,
        });
      }
    }
    loadPricing();
  }, []);

  // When booker info changes and booker is attending, sync Attendee #1
  useEffect(() => {
    if (isBookerAttending) {
      setAttendees((prev) => {
        if (prev.length === 0) return prev;
        const [first, ...rest] = prev;
        return [
          {
            ...first,
            fullName: bookerName,
            email: bookerEmail,
            phone: bookerPhone,
          },
          ...rest,
        ];
      });
    }
  }, [isBookerAttending, bookerName, bookerEmail, bookerPhone]);

  // Handle Booker Attendance Toggle
  const handleToggleBookerAttending = (checked: boolean) => {
    setIsBookerAttending(checked);
    if (!checked) {
      // Clear Attendee #1 if it currently matches booker, allowing them to enter a colleague
      setAttendees((prev) => {
        if (prev.length === 0) return prev;
        const [first, ...rest] = prev;
        if (
          first.fullName === bookerName &&
          first.email === bookerEmail &&
          first.phone === bookerPhone
        ) {
          return [
            {
              ...first,
              fullName: '',
              email: '',
              phone: '',
              jobTitle: '',
            },
            ...rest,
          ];
        }
        return prev;
      });
    } else {
      // Restore Attendee #1 to booker details
      setAttendees((prev) => {
        if (prev.length === 0) return prev;
        const [first, ...rest] = prev;
        return [
          {
            ...first,
            fullName: bookerName,
            email: bookerEmail,
            phone: bookerPhone,
          },
          ...rest,
        ];
      });
    }
  };

  const handleAddAttendee = () => {
    setAttendees((prev) => [
      ...prev,
      {
        id: `att_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        fullName: '',
        email: '',
        phone: '',
        jobTitle: '',
        tier: 'conference_delegate',
      },
    ]);
  };

  const handleRemoveAttendee = (id: string) => {
    if (attendees.length <= 1) return;
    setAttendees((prev) => prev.filter((a) => a.id !== id));
  };

  const handleUpdateAttendee = (
    id: string,
    field: keyof AttendeeEntry,
    value: string
  ) => {
    setAttendees((prev) =>
      prev.map((att) => (att.id === id ? { ...att, [field]: value } : att))
    );
  };

  const activePhase = scheduleData?.activePhase;

  // Aggregate Pricing Calculations
  const getTierPrice = (tier: 'trade_visitor' | 'conference_delegate' | 'vip') => {
    if (activePhase?.prices?.[tier] !== undefined) {
      return activePhase.prices[tier];
    }
    return tier === 'trade_visitor' ? 999 : tier === 'vip' ? 11999 : 4999;
  };

  const getTierSavings = (tier: 'trade_visitor' | 'conference_delegate' | 'vip') => {
    if (activePhase?.savingsVsSpot?.[tier] !== undefined) {
      return activePhase.savingsVsSpot[tier];
    }
    const spot = scheduleData?.spotPrices?.[tier];
    const currentPrice = getTierPrice(tier);
    if (spot && spot > currentPrice) {
      return spot - currentPrice;
    }
    return 0;
  };

  const totalBasePrice = attendees.reduce((sum, att) => sum + getTierPrice(att.tier), 0);
  const totalGstAmount = Math.round(totalBasePrice * 0.18);
  const totalPayable = totalBasePrice + totalGstAmount;
  const totalSavings = attendees.reduce((sum, att) => sum + getTierSavings(att.tier), 0);

  // Grouped summary counts
  const tierCounts = attendees.reduce(
    (acc, att) => {
      acc[att.tier] = (acc[att.tier] || 0) + 1;
      return acc;
    },
    { trade_visitor: 0, conference_delegate: 0, vip: 0 } as Record<string, number>
  );

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    setOrderError(null);

    // Validation
    if (!bookerName.trim()) {
      setOrderError('Please provide the full name for the booking contact.');
      setProcessing(false);
      return;
    }
    if (!bookerEmail.trim() || !bookerEmail.includes('@')) {
      setOrderError('Please provide a valid email address for the booking contact.');
      setProcessing(false);
      return;
    }
    if (!bookerPhone.trim()) {
      setOrderError('Please provide a contact phone number for the booking coordinator.');
      setProcessing(false);
      return;
    }

    // Validate each attendee
    for (let i = 0; i < attendees.length; i++) {
      const att = attendees[i];
      if (!att.fullName.trim()) {
        setOrderError(`Please specify the full name for Attendee #${i + 1}.`);
        setProcessing(false);
        return;
      }
      if (!att.email.trim() || !att.email.includes('@')) {
        setOrderError(`Please specify a valid email address for Attendee #${i + 1}.`);
        setProcessing(false);
        return;
      }
      if (!att.phone.trim()) {
        setOrderError(`Please specify a mobile phone number for Attendee #${i + 1}.`);
        setProcessing(false);
        return;
      }
    }

    try {
      const orderPayload = {
        booker: {
          fullName: bookerName.trim(),
          email: bookerEmail.trim(),
          phone: bookerPhone.trim(),
          companyName: companyName.trim() || undefined,
          gstin: gstin.trim() || undefined,
          isAttending: isBookerAttending,
        },
        attendees: attendees.map((a) => ({
          fullName: a.fullName.trim(),
          email: a.email.trim(),
          phone: a.phone.trim(),
          jobTitle: a.jobTitle.trim() || undefined,
          tier: a.tier,
        })),
      };

      const order = await createAttendeeOrder(orderPayload);

      const scriptLoaded = await loadRazorpayScript();
      const RazorpayConstructor = (window as any).Razorpay;

      if (scriptLoaded && RazorpayConstructor && order.keyId && !order.keyId.includes('placeholder')) {
        const rzp = new RazorpayConstructor({
          key: order.keyId,
          amount: order.amount,
          currency: 'INR',
          name: 'IIAB Expo & Conference 2027',
          description: `${attendees.length} Delegate Pass${attendees.length > 1 ? 'es' : ''} • JWCC Mumbai`,
          order_id: order.razorpayOrderId,
          prefill: {
            name: bookerName,
            email: bookerEmail,
            contact: bookerPhone,
          },
          notes: {
            orderId: order.razorpayOrderId,
            attendeeCount: String(attendees.length),
            isBookerAttending: String(isBookerAttending),
            profileToken: order.profileToken,
          },
          theme: {
            color: '#0A3D27',
          },
          handler: async function (response: any) {
            // Send Razorpay's signed proof to our server so passes are issued now,
            // rather than waiting for the webhook. If this fails (e.g. network),
            // the webhook still confirms it and the next page keeps checking.
            try {
              await verifyAttendeePayment({
                razorpayOrderId: response.razorpay_order_id || order.razorpayOrderId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
            } catch {
              // Non-fatal: RegistrationCompletePage polls until the webhook lands.
            }
            try {
              confetti({
                particleCount: 80,
                spread: 70,
                origin: { y: 0.6 },
              });
            } catch {
              // Confetti fallback
            }
            navigate(`/registration-complete?orderId=${order.razorpayOrderId}&token=${order.profileToken}`);
          },
          modal: {
            ondismiss: function () {
              setProcessing(false);
            },
          },
        });

        rzp.on('payment.failed', function (resp: any) {
          setOrderError(`Payment declined: ${resp.error?.description || 'Transaction failed'}`);
          setProcessing(false);
        });

        rzp.open();
      } else if (import.meta.env.DEV) {
        // Local development only: no Razorpay keys configured
        setTimeout(() => {
          navigate(`/registration-complete?orderId=${order.razorpayOrderId}&token=${order.profileToken}`);
        }, 1200);
      } else {
        setOrderError('Payment checkout could not be loaded. Please refresh the page and try again.');
        setProcessing(false);
      }
    } catch (err: any) {
      setOrderError(err.message || 'Failed to initialize order checkout.');
      setProcessing(false);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupToken.trim()) return;

    setLookupLoading(true);
    setLookupError(null);
    setFoundBadge(null);

    try {
      const badge = await fetchAttendeeBadge(lookupToken.trim());
      setFoundBadge(badge);
    } catch (err: any) {
      setLookupError(err.message || 'Pass token not found. Please verify your receipt email.');
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Quick Switcher Banner: Already Registered */}
      <div className="bg-gradient-to-r from-emerald-50/70 via-white to-[#FAF5EA]/50 border border-emerald-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#0A3D27] border border-emerald-200 flex items-center justify-center shrink-0 shadow-2xs">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <span>Already registered for IIAB 2027?</span>
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-[#0A3D27] border border-emerald-200">
                Existing Pass
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Retrieve your digital QR pass, download/print badges, or update your B2B profile survey.
            </p>
          </div>
        </div>

        <Link
          to="/my-iiab"
          className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-emerald-950/20 group cursor-pointer"
        >
          <span>Already Registered? Access My IIAB</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 pb-6 space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[#0A3D27] text-xs font-bold">
          <Ticket className="w-3.5 h-3.5" />
          <span>New Visitor & Delegate Registration • {eventInfo.edition}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-[#0F172A] tracking-tight">
          Visitor & Delegate Pass Registration
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl leading-relaxed">
          Register individual passes or multiple delegates for your organization to attend India's premier B2B Agri Inputs
          Convention at Jio World Convention Centre (JWCC), BKC, Mumbai. You can book on behalf of your team even if you
          are not attending yourself.
        </p>
      </div>

      {activeTab === 'lookup' ? (
        /* Badge Retrieval Section */
        <div className="max-w-2xl mx-auto space-y-8 py-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-8 space-y-6 shadow-sm text-center">
            <QrCode className="w-12 h-12 text-[#0A3D27] mx-auto" />
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-slate-900">Retrieve Your Digital Pass</h2>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Enter your 12-character IIAB26 token from your confirmation email or SMS to access your digital badge
                and turnstile credentials.
              </p>
            </div>

            <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                required
                value={lookupToken}
                onChange={(e) => setLookupToken(e.target.value)}
                placeholder="e.g. IIAB26-XyZ9810a"
                className="flex-1 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs font-mono uppercase focus:outline-none focus:border-[#0A3D27] focus:bg-white"
              />
              <button
                type="submit"
                disabled={lookupLoading}
                className="px-6 py-3 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold text-xs transition-colors cursor-pointer shrink-0"
              >
                {lookupLoading ? 'Searching...' : 'Find Badge'}
              </button>
            </form>

            {lookupError && (
              <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 p-3 rounded-xl">
                {lookupError}
              </p>
            )}
          </div>

          {foundBadge && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-900 text-center">Found Badge Record</h3>
              <DigitalBadge attendee={foundBadge} />
            </div>
          )}
        </div>
      ) : isAppStoreReaderMode() ? (
        <div className="max-w-2xl mx-auto bg-white border border-emerald-200 rounded-3xl p-8 shadow-sm text-center space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-[#0A3D27]/10 text-[#0A3D27] flex items-center justify-center mx-auto">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="space-y-2">
            <span className="inline-block px-3 py-1 rounded-full bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[11px] font-bold uppercase tracking-wider">
              IIAB 2027 Companion App
            </span>
            <h2 className="text-2xl font-black text-slate-900">
              Access Your Registered Event Pass &amp; B2B Portal
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
              The IIAB 2027 mobile app is your official companion for physical badge collection codes, 1-on-1 B2B meetings, exhibitor lead scanning, and Pavilion 3 navigation at Jio World Convention Centre, Mumbai.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Link
              to="/my-iiab"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] font-bold text-xs inline-flex items-center justify-center gap-2 shadow-sm"
            >
              <span>Sign In to Delegate / Speaker Portal</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/exhibitor-portal"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-bold text-xs inline-flex items-center justify-center gap-2"
            >
              <Building2 className="w-4 h-4 text-[#0A3D27]" />
              <span>Exhibitor Lead Scanner</span>
            </Link>
          </div>
        </div>
      ) : (
        /* Main Purchase & Multi-Attendee Flow */
        <div className="space-y-10">
          {/* Active Pricing Phase Banner */}
          {activePhase && (
            <div className="bg-gradient-to-r from-emerald-50/80 via-white to-[#FAF5EA]/60 border border-emerald-200 rounded-3xl p-5 sm:p-7 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
                  <TrendingDown className="w-6 h-6 text-[#0A3D27]" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-[#0A3D27] uppercase tracking-wider">
                      Current Window: {activePhase.name}
                    </span>
                    <span className="bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                      {activePhase.badge}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 leading-snug">
                    {activePhase.description}. Advance pricing closes{' '}
                    <strong>
                      {new Date(activePhase.endDate).toLocaleDateString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </strong>.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowScheduleModal(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white hover:bg-slate-50 border border-slate-200 text-xs text-[#0A3D27] font-bold transition-colors cursor-pointer shadow-2xs"
              >
                <span>View All 6 Price Windows</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Pass Tiers Overview Grid (Reference Cards) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Tier 1: Trade Visitor */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4 shadow-xs flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Expo Floor Pass
                  </span>
                  {activePhase && (
                    <span className="text-[10px] font-bold text-[#0A3D27] bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                      Save ₹{getTierSavings('trade_visitor').toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-black text-slate-900">Trade Visitor</h3>
                <div className="flex items-baseline gap-2 pt-1">
                  <span className="text-3xl font-black text-slate-900">
                    ₹{getTierPrice('trade_visitor').toLocaleString('en-IN')}
                  </span>
                  <span className="text-xs text-slate-500 font-medium">+ 18% GST</span>
                </div>
                <div className="text-[11px] text-slate-400 line-through">
                  Spot Rate: ₹3,000 + GST
                </div>
                <p className="text-xs text-slate-600">
                  Ideal for distributors, dealers, retailers, chemical traders, and progressive growers.
                </p>
                <ul className="space-y-2 text-xs text-slate-700 border-t border-slate-100 pt-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Full 2-day access to Pavilion 3 Exhibition Floor</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>B2B Trading Tables & Supplier networking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Instant Digital QR pass & turnstile badge</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Tier 2: Conference Delegate (Recommended) */}
            <div className="bg-white border-2 border-[#0A3D27] rounded-3xl p-6 space-y-4 shadow-md relative flex flex-col justify-between">
              <span className="absolute -top-3 left-6 bg-[#FAF5EA] border border-[#E6C687] text-[#785613] font-bold text-[10px] uppercase px-3 py-0.5 rounded-full shadow-2xs">
                Most Popular • Recommended
              </span>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#0A3D27] uppercase tracking-wider">
                    Full Convention Pass
                  </span>
                  {activePhase && (
                    <span className="text-[10px] font-bold text-[#0A3D27] bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                      Save ₹{getTierSavings('conference_delegate').toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-black text-slate-900">Industry Professional Pass</h3>
                <div className="flex items-baseline gap-2 pt-1">
                  <span className="text-3xl font-black text-[#0A3D27]">
                    ₹{getTierPrice('conference_delegate').toLocaleString('en-IN')}
                  </span>
                  <span className="text-xs text-slate-500 font-medium">+ 18% GST</span>
                </div>
                <div className="text-[11px] text-slate-400 line-through">
                  Spot Rate: ₹10,500 + GST
                </div>
                <p className="text-xs text-slate-600">
                  For agrochemical executives, agronomists, researchers, and technical heads.
                </p>
                <ul className="space-y-2 text-xs text-slate-700 border-t border-slate-100 pt-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Access to all 12+ Keynote & Technical sessions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Full Exhibition Floor & Pavilion 3 access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Networking Luncheon & High Tea at JWCC</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
                    <span>Delegate Kit with Whitepapers & Speaker Dossier</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Tier 3: VIP All-Access */}
            <div className="bg-white border border-[#C5A059] rounded-3xl p-6 space-y-4 shadow-xs relative flex flex-col justify-between">
              <span className="absolute -top-3 left-6 bg-[#FAF5EA] border border-[#E6C687] text-[#785613] font-bold text-[10px] uppercase px-3 py-0.5 rounded-full shadow-2xs">
                Executive Privileges
              </span>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#785613] uppercase tracking-wider">
                    VIP Executive
                  </span>
                  {activePhase && (
                    <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] px-2 py-0.5 rounded-md border border-[#E6C687]">
                      Save ₹{getTierSavings('vip').toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-black text-slate-900">Executive Access Pass</h3>
                <div className="flex items-baseline gap-2 pt-1">
                  <span className="text-3xl font-black text-[#785613]">
                    ₹{getTierPrice('vip').toLocaleString('en-IN')}
                  </span>
                  <span className="text-xs text-slate-500 font-medium">+ 18% GST</span>
                </div>
                <div className="text-[11px] text-slate-400 line-through">
                  Spot Rate: ₹20,000 + GST
                </div>
                <p className="text-xs text-slate-600">
                  For CEOs, Managing Directors, government officials, and venture investors.
                </p>
                <ul className="space-y-2 text-xs text-slate-700 border-t border-slate-100 pt-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
                    <span>Pavilion 3 VIP Executive Lounge access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
                    <span>Exclusive Ministerial & Dignitaries Gala Dinner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
                    <span>Reserved Front-Row seating & VIP fast-track badge</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Registration Form: Booker Details + Multi-Attendee Roster */}
          <form onSubmit={handleCheckout} className="space-y-8">
            {orderError && (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-xs text-rose-800 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                <span>{orderError}</span>
              </div>
            )}

            {/* SECTION 1: PRIMARY BOOKER & BILLING DETAILS */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A3D27] uppercase tracking-wider">
                  <CreditCard className="w-3.5 h-3.5" />
                  Step 1 • Primary Booker & Billing Coordinator
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900">
                  Billing Contact & Company Information
                </h2>
                <p className="text-xs text-slate-500">
                  Invoices, payment receipts, and overall booking management will be linked to this contact.
                </p>
              </div>

              {/* Attending Toggle Switch Card */}
              <div
                onClick={() => handleToggleBookerAttending(!isBookerAttending)}
                className={`p-4 sm:p-5 rounded-2xl border transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                  isBookerAttending
                    ? 'bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-200'
                    : 'bg-[#F8F9F6] border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start sm:items-center gap-3.5">
                  <div
                    className={`w-6 h-6 rounded-lg border flex items-center justify-center shrink-0 transition-colors ${
                      isBookerAttending
                        ? 'bg-[#0A3D27] border-[#0A3D27] text-white'
                        : 'bg-white border-slate-300 text-transparent'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-sm font-bold text-slate-900">
                      I am attending this event personally
                    </span>
                    <p className="text-xs text-slate-600">
                      {isBookerAttending
                        ? 'Your credentials will automatically be assigned to Attendee #1. You can add more colleagues below.'
                        : 'You are booking purely on behalf of others / team. No personal pass will be issued for you unless added below.'}
                    </p>
                  </div>
                </div>

                <div className="shrink-0">
                  <span
                    className={`text-[11px] font-bold px-3 py-1 rounded-full ${
                      isBookerAttending
                        ? 'bg-[#0A3D27] text-white'
                        : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {isBookerAttending ? 'Attending as Attendee #1' : 'Booking for Others Only'}
                  </span>
                </div>
              </div>

              {/* Booker Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    Booker / Purchaser Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={bookerName}
                    onChange={(e) => setBookerName(e.target.value)}
                    placeholder="e.g. Ramesh Chandra Verma"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    Booker Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={bookerEmail}
                    onChange={(e) => setBookerEmail(e.target.value)}
                    placeholder="e.g. ramesh@agri-inputs.in"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    Booker Mobile Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={bookerPhone}
                    onChange={(e) => setBookerPhone(e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    Company / Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Bharat Agrochemicals LLP"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <Receipt className="w-3.5 h-3.5 text-slate-400" />
                      GSTIN for Tax Invoice (Optional)
                    </label>
                    <span className="text-[11px] text-slate-500">18% GST Input Credit</span>
                  </div>
                  <input
                    type="text"
                    maxLength={15}
                    value={gstin}
                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    placeholder="e.g. 27AAAAA0000A1Z5"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs font-mono uppercase focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: ATTENDEES ROSTER (MULTIPLE PASSES) */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A3D27] uppercase tracking-wider">
                    <Users className="w-3.5 h-3.5" />
                    Step 2 • Attendees & Pass Selection
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900">
                    Registered Attendees ({attendees.length} Pass{attendees.length > 1 ? 'es' : ''})
                  </h2>
                  <p className="text-xs text-slate-500">
                    Each attendee will receive their own personalized digital badge and unique entry QR code.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAddAttendee}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] border border-emerald-200 font-bold text-xs transition-colors cursor-pointer shrink-0 shadow-2xs"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>+ Add Another Attendee</span>
                </button>
              </div>

              {/* Attendee Cards List */}
              <div className="space-y-6">
                {attendees.map((attendee, index) => {
                  const isFirstAndBooker = isBookerAttending && index === 0;

                  return (
                    <div
                      key={attendee.id}
                      className="bg-[#F8F9F6] border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-5 transition-all"
                    >
                      {/* Card Header */}
                      <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                        <div className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-lg bg-[#0A3D27] text-white font-bold text-xs flex items-center justify-center">
                            #{index + 1}
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            Attendee {index + 1}
                          </span>
                          {isFirstAndBooker ? (
                            <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-100 text-[#0A3D27] border border-emerald-200">
                              Booker (Attending)
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-slate-500 uppercase px-2 py-0.5 rounded-full bg-white border border-slate-200">
                              Delegate Pass
                            </span>
                          )}
                        </div>

                        {attendees.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttendee(attendee.id)}
                            className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 transition-colors cursor-pointer px-2 py-1 rounded-lg hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Remove</span>
                          </button>
                        )}
                      </div>

                      {/* Attendee Credentials Inputs */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            Full Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={attendee.fullName}
                            onChange={(e) =>
                              handleUpdateAttendee(attendee.id, 'fullName', e.target.value)
                            }
                            placeholder="e.g. Priya Sharma"
                            className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            Work Email *
                          </label>
                          <input
                            type="email"
                            required
                            value={attendee.email}
                            onChange={(e) =>
                              handleUpdateAttendee(attendee.id, 'email', e.target.value)
                            }
                            placeholder="e.g. priya@agricorp.com"
                            className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            Mobile Phone *
                          </label>
                          <input
                            type="tel"
                            required
                            value={attendee.phone}
                            onChange={(e) =>
                              handleUpdateAttendee(attendee.id, 'phone', e.target.value)
                            }
                            placeholder="e.g. +91 98200 12345"
                            className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                            Designation / Job Title
                          </label>
                          <input
                            type="text"
                            value={attendee.jobTitle}
                            onChange={(e) =>
                              handleUpdateAttendee(attendee.id, 'jobTitle', e.target.value)
                            }
                            placeholder="e.g. Head of Procurement"
                            className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>
                      </div>

                      {/* Attendee Tier Selection */}
                      <div className="space-y-2 pt-2">
                        <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                          <span>Select Pass Tier for Attendee #{index + 1} *</span>
                          <span className="text-[11px] text-slate-500 font-normal">
                            Individual pass rights apply at venue turnstiles
                          </span>
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Option 1: Trade Visitor */}
                          <div
                            onClick={() =>
                              handleUpdateAttendee(attendee.id, 'tier', 'trade_visitor')
                            }
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                              attendee.tier === 'trade_visitor'
                                ? 'bg-white border-[#0A3D27] ring-2 ring-[#0A3D27]/20 shadow-xs'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-900">
                                  Trade Visitor
                                </span>
                                <span className="text-xs font-black text-slate-900 font-mono">
                                  ₹{getTierPrice('trade_visitor').toLocaleString('en-IN')}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 leading-tight">
                                Pavilion 3 Exhibition Floor & B2B table networking.
                              </p>
                            </div>
                            <div className="pt-2 text-[10px] text-slate-400 flex items-center justify-between">
                              <span>+18% GST</span>
                              {attendee.tier === 'trade_visitor' && (
                                <span className="text-[#0A3D27] font-bold">Selected</span>
                              )}
                            </div>
                          </div>

                          {/* Option 2: Conference Delegate */}
                          <div
                            onClick={() =>
                              handleUpdateAttendee(attendee.id, 'tier', 'conference_delegate')
                            }
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between relative ${
                              attendee.tier === 'conference_delegate'
                                ? 'bg-white border-[#0A3D27] ring-2 ring-[#0A3D27]/20 shadow-xs'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <span className="absolute -top-2 right-3 bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[9px] font-bold px-1.5 py-0.2 rounded-full">
                              Popular
                            </span>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-[#0A3D27]">
                                  Industry Professional Pass
                                </span>
                                <span className="text-xs font-black text-[#0A3D27] font-mono">
                                  ₹{getTierPrice('conference_delegate').toLocaleString('en-IN')}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 leading-tight">
                                Full Convention, all sessions, exhibition floor & luncheons.
                              </p>
                            </div>
                            <div className="pt-2 text-[10px] text-slate-400 flex items-center justify-between">
                              <span>+18% GST</span>
                              {attendee.tier === 'conference_delegate' && (
                                <span className="text-[#0A3D27] font-bold">Selected</span>
                              )}
                            </div>
                          </div>

                          {/* Option 3: VIP Pass */}
                          <div
                            onClick={() =>
                              handleUpdateAttendee(attendee.id, 'tier', 'vip')
                            }
                            className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                              attendee.tier === 'vip'
                                ? 'bg-white border-[#C5A059] ring-2 ring-[#C5A059]/20 shadow-xs'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-[#785613]">
                                  Executive Access Pass
                                </span>
                                <span className="text-xs font-black text-[#785613] font-mono">
                                  ₹{getTierPrice('vip').toLocaleString('en-IN')}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 leading-tight">
                                VIP Executive Lounge, Gala Dinner, reserved front-row seating.
                              </p>
                            </div>
                            <div className="pt-2 text-[10px] text-slate-400 flex items-center justify-between">
                              <span>+18% GST</span>
                              {attendee.tier === 'vip' && (
                                <span className="text-[#785613] font-bold">Selected</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Attendee Dashed Callout */}
              <button
                type="button"
                onClick={handleAddAttendee}
                className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#0A3D27] hover:bg-emerald-50/40 text-slate-600 hover:text-[#0A3D27] transition-all flex items-center justify-center gap-2 text-xs font-bold cursor-pointer"
              >
                <UserPlus className="w-4 h-4" />
                <span>+ Add Another Attendee / Delegate Pass to this Order</span>
              </button>
            </div>

            {/* SECTION 3: AGGREGATE ORDER SUMMARY & CHECKOUT */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A3D27] uppercase tracking-wider">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Order Summary & Payment
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900">
                  Consolidated Order Breakdown
                </h2>
              </div>

              {/* Itemized Table */}
              <div className="bg-[#F8F9F6] border border-slate-200 rounded-2xl p-5 space-y-3">
                <div className="space-y-2 border-b border-slate-200 pb-3">
                  {tierCounts.conference_delegate > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-700">
                      <span>
                        {tierCounts.conference_delegate}x Industry Professional Pass (
                        {activePhase?.name})
                      </span>
                      <span className="font-mono font-semibold">
                        ₹
                        {(
                          tierCounts.conference_delegate *
                          getTierPrice('conference_delegate')
                        ).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}

                  {tierCounts.vip > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-700">
                      <span>
                        {tierCounts.vip}x Executive Access Pass ({activePhase?.name})
                      </span>
                      <span className="font-mono font-semibold">
                        ₹{(tierCounts.vip * getTierPrice('vip')).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}

                  {tierCounts.trade_visitor > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-700">
                      <span>
                        {tierCounts.trade_visitor}x Trade Visitor Pass ({activePhase?.name})
                      </span>
                      <span className="font-mono font-semibold">
                        ₹
                        {(
                          tierCounts.trade_visitor * getTierPrice('trade_visitor')
                        ).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>Subtotal (Base Price for {attendees.length} pass{attendees.length > 1 ? 'es' : ''})</span>
                  <span className="font-mono">₹{totalBasePrice.toLocaleString('en-IN')}</span>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Applicable GST (18%)</span>
                  <span className="font-mono">₹{totalGstAmount.toLocaleString('en-IN')}</span>
                </div>

                {totalSavings > 0 && (
                  <div className="flex items-center justify-between text-xs text-[#0A3D27] font-semibold border-t border-slate-200 pt-2">
                    <span>Advance Booking Savings vs Spot Rates</span>
                    <span className="font-mono font-bold">
                      - ₹{totalSavings.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-base font-black text-slate-900 border-t border-slate-200 pt-3">
                  <div>
                    <span>Total Payable Amount</span>
                    <span className="block text-[11px] font-normal text-slate-500">
                      Includes 18% GST • Single consolidated tax invoice
                    </span>
                  </div>
                  <span className="text-xl text-[#0A3D27] font-mono">
                    ₹{totalPayable.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={processing}
                className="w-full py-4 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-extrabold text-sm shadow-md shadow-emerald-950/20 flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Securing Registration & Payment...</span>
                  </>
                ) : (
                  <>
                    <span>
                      Proceed to Payment • ₹{totalPayable.toLocaleString('en-IN')} ({attendees.length}{' '}
                      Pass{attendees.length > 1 ? 'es' : ''})
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-slate-500 gap-2 border-t border-slate-100 pt-4">
                <span>
                  Payments encrypted via Razorpay India. Invoicing by RAY One Agri Consulting LLP (GSTIN:{' '}
                  {eventInfo.organizer.gstin}).
                </span>
                <span className="font-bold text-[#0A3D27]">
                  Instant digital credentials dispatched upon payment
                </span>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* 6 Cutoff Phases Modal */}
      {showScheduleModal && scheduleData && (
        <div className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-slate-900">
                  IIAB Expo 2027 Advance Pricing Schedule
                </h3>
                <p className="text-xs text-slate-500">5 Dynamic Advance Windows + Spot Onsite Rate</p>
              </div>
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center text-xs font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {scheduleData.allPhases.map((phase) => {
                const isCurrent = phase.id === scheduleData.activePhase?.id;
                return (
                  <div
                    key={phase.id}
                    className={`p-4 rounded-2xl border transition-all ${
                      isCurrent
                        ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900">{phase.name}</span>
                        {isCurrent && (
                          <span className="bg-[#0A3D27] text-white font-bold text-[9px] uppercase px-2 py-0.5 rounded-full">
                            Active Window
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-mono text-[#0A3D27] font-bold">{phase.badge}</span>
                    </div>

                    <p className="text-xs text-slate-600 mb-3">{phase.description}</p>

                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-white p-2 rounded-xl border border-slate-200">
                        <span className="block text-[10px] text-slate-500">Trade Visitor</span>
                        <strong className="text-slate-900 font-mono">₹{phase.prices?.trade_visitor ?? 0}</strong>
                      </div>
                      <div className="bg-white p-2 rounded-xl border border-emerald-200">
                        <span className="block text-[10px] text-[#0A3D27]">Industry Pro</span>
                        <strong className="text-[#0A3D27] font-mono">₹{phase.prices?.conference_delegate ?? 0}</strong>
                      </div>
                      <div className="bg-white p-2 rounded-xl border border-[#E6C687]">
                        <span className="block text-[10px] text-[#785613]">Executive Pass</span>
                        <strong className="text-[#785613] font-mono">₹{phase.prices?.vip ?? 0}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                className="px-6 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-colors cursor-pointer"
              >
                Close Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegisterPage;
